/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  Box,
  Switch,
  Text,
  theme,
  SwitchProps,
  Input,
  Label,
  Tooltip,
  FlexContainer,
} from "@nordcloud/gnui";
import { LoaderWrap } from "~/components";
import { inflect } from "~/tools";

type Props = SwitchProps & {
  label: string;
  description: string;
  onChange: (event: React.MouseEvent<HTMLDivElement>) => void;
  loading?: boolean;
  children?: React.ReactNode;
};

export function BoxSwitchInput({
  label,
  description,
  disabled = false,
  onChange,
  loading = false,
  children = null,
  ...props
}: Props) {
  return (
    <Box
      boxStyle="lightGrey"
      css={{ filter: disabled ? "grayscale(100%)" : "none" }}
    >
      <LoaderWrap inContent loading={loading}>
        <FlexContainer wrap="wrap" alignItems="flex-start">
          <Switch {...props} disabled={disabled} onChange={onChange} />
          <div css={{ padding: `0 ${theme.spacing.spacing04}`, flex: 1 }}>
            <FlexContainer
              style={{ cursor: "pointer" }}
              alignItems="flex-start"
              direction="column"
              onClick={disabled ? () => null : onChange}
            >
              <Text tag="div" size="md" weight="medium">
                {label}
              </Text>
              <Text
                tag="div"
                size="sm"
                weight="regular"
                color={theme.color.text.text02}
              >
                {description}
              </Text>
            </FlexContainer>
            {children}
          </div>
        </FlexContainer>
      </LoaderWrap>
    </Box>
  );
}

type CustomNumberInputProps = {
  id: string;
  label: string;
  value: string;
  unit: string;
  disabled?: boolean;
  tooltipCaption: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export function CustomNumberInput({
  id,
  label,
  value,
  unit,
  tooltipCaption,
  onChange,
  disabled = false,
}: CustomNumberInputProps) {
  const unitText =
    unit.toLowerCase() === "percentage" ? "%" : unit.slice(0, -1);
  const pluralUnitText =
    unit.toLowerCase() === "percentage" ? "%" : `${unitText}s`;

  return (
    <FlexContainer
      alignItems="baseline"
      css={{ marginTop: theme.spacing.spacing03 }}
    >
      <div css={{ width: "auto" }}>
        <Label htmlFor={id} name={label} />
      </div>
      <Tooltip
        caption={tooltipCaption}
        status="warning"
        hideTimeout={0}
        minWidth="16rem"
      >
        <div css={{ maxWidth: "70px", margin: `0 ${theme.spacing.spacing04}` }}>
          <Input
            id={id}
            type="number"
            value={value}
            disabled={disabled}
            onChange={onChange}
          />
        </div>
      </Tooltip>
      <div css={{ flex: 1 }}>
        {inflect(unitText, pluralUnitText)(parseInt(value, 10))}
      </div>
    </FlexContainer>
  );
}
